import {registerBrick, CloudObject, QueryResult} from 'olympe';
import {
    cssToSxProps,
    getLogger,
    ifNotNull,
    ifNotTransparent,
    jsonToSxProps,
    ReactBrick,
    useProperty
} from '@olympeio/core'
import React, { useRef } from "react";
import ChartGeo, {ChoroplethChart} from 'chartjs-chart-geo';
import {of} from "rxjs";
import Box from "@mui/material/Box";
import CircularProgress from '@mui/material/CircularProgress';
import {switchMap} from "rxjs/operators";
import {getCountryProjection, parseJSON} from "./utils/utils";
import {merge as lodashDeepMerge} from "lodash";

export default class MapChart extends ReactBrick {

    static maps = new Map();


    /**
     * Subscribe to the map, on each change fetch the vectorial data of the new map
     *
     * By overriding init() we can set the context variable used in the rendering of the React component
     * and we don't need to override the setupExecution() since Visual Component are rendered only once
     * and then react takes care of further updates
     *
     * @override
     */
    init($) {
        this.logger = getLogger('MapChart');

        // For the given map configuration, fetch the data
        $.observe('map', false).pipe(
            switchMap((mapUrl) => {
                const vectorData = MapChart.maps.get(mapUrl);
                if (vectorData) {
                    return of(vectorData);
                } else {
                    $.set('mapData', null);
                    return fetch(mapUrl)
                        .then((r) => r.json())
                        .then((mapData) => {
                            MapChart.maps.set($.get('map'), mapData);
                            return mapData;
                        });
                }
            })

        ).subscribe({
            next: (data)  => {
                $.set('error', null);
                $.set('mapData', data);
            },
            error: (error) => {
                this.logger.error('An error occurred while loading the map', error);
                $.set('error', 'An error occurred while loading the map');
            }
        })
    }



    /**
     * @override
     * @param {!BrickContext} $
     */
    static getReactComponent($) {
        return () => {
            this.logger = getLogger('MapChart');
            const canvasRef = useRef(null);
            const [chart, setChart] = React.useState(null);

            const mapData = useProperty($, 'mapData', false);
            const refresh = useProperty($, 'refresh', false);
            const rawData = useProperty($, 'data', false);
            const width = useProperty($, 'Width', false);
            const height = useProperty($, 'Height', false);
            const colorScale = useProperty($, 'color scale', false);
            const legendPosition = useProperty($, 'legend position', false);
            const mapBorderColor = useProperty($, 'map border color', false);
            const additionalJsonConfig = useProperty($, 'config JSON', false);
            const error = useProperty($, 'error', false);
            const keyPath = useProperty($, 'key path', false);
            const valuePath = useProperty($, 'value path', false);
            const scaleQuantity = useProperty($, 'color steps', false);

            const validatedRawData = React.useMemo(() => {
                return rawData instanceof Array ? rawData : rawData instanceof QueryResult ? rawData.toArray() : [];
            }, [rawData]);
            
            React.useEffect(() => {
                const mappedRawData = validatedRawData.map(dataPoint => 
                    dataPoint instanceof CloudObject ? dataPoint.toJSON() : dataPoint
                );
            
                if (mappedRawData.length > 0 && !mappedRawData.every(item => typeof item[valuePath] === 'number')) {
                    this.logger.warn('One or more items in the data field have the wrong value type. Each item must have a number at its value path.');
                }
            }, [validatedRawData, valuePath]);

            React.useEffect(()=>{
                // Check if canvas and data are ready
                let canvas = canvasRef.current;
                if(!canvas || !mapData) return;

                // Get the actual width of the map according to the size of the component (the map ratio is always 2:1)
                const mapWidth = Math.min(width, height*2) === width ? width : height*2;
                // On the 2:1 map, one degree in latitude is the same size as one in longitude given that there are 360lat vs 180long
                const oneDegree = mapWidth / 360;

                // Get the data helping to center & move the map on the selected country
                const countryCode = Object.keys(mapData.objects)[0];
                const projection = getCountryProjection(countryCode, oneDegree);
                const mapElements = ChartGeo.topojson.feature(mapData, mapData.objects[countryCode]).features;

                // Small mapping to know where to display the legend properly
                let align;
                switch(legendPosition) {
                    case 'top-right':
                    case 'bottom-right':
                    case 'right':
                        align = 'left';
                        break;
                    case 'top-left':
                    case 'bottom-left':
                    case 'left':
                        align = 'right';
                        break;
                    case 'bottom':
                        align = 'top';
                        break;
                    case 'top':
                        align = 'bottom';
                        break;
                    default:
                        align = 'right';
                        break;
                }

                const config = {
                    type: 'choropleth',
                    data: {
                        labels: mapElements.map((d) => d.properties.name),
                        datasets: [{
                            label: 'Not used',
                            datalabels: {
                                display: false,
                            },
                            data: mapElements.map((d) => {

                                let value = undefined;
                                let foundData = validatedRawData.filter(dataPoint => {
                                    if(dataPoint instanceof CloudObject){
                                        dataPoint = dataPoint.toJSON()
                                    }    
                                    return dataPoint[keyPath] === d.id;
                                })?.[0];
                                if(foundData){
                                    if(foundData instanceof CloudObject){
                                        foundData = foundData.toJSON();
                                    }
                                    value = foundData[valuePath];
                                } else {
                                    // Fallback on the name if ID not found
                                    let foundPropertyData = validatedRawData.filter(dataPoint => {
                                        if(dataPoint instanceof CloudObject){
                                            dataPoint = dataPoint.toJSON()
                                        }
                                        
                                        return dataPoint[keyPath] === d.properties.name;
                                    })?.[0];

                                    if(foundPropertyData) {
                                        if(foundPropertyData instanceof CloudObject){
                                            foundPropertyData = foundPropertyData.toJSON();
                                        }
                                        value = foundPropertyData[valuePath];
                                    }
                                }

                                return {
                                    feature: d,
                                    value: value ?? 0,
                                    name: d.properties?.name
                                }
                            }),
                            borderColor: mapBorderColor,
                        }]
                    },
                    options: {
                        showOutline: false,
                        showGraticule: false,
                        plugins: {
                            legend: {
                                display: false
                            },
                        },
                        scales: {
                            projection: {
                                axis: 'x',
                                projection: 'equirectangular',
                                projectionScale: projection.scale,
                                projectionOffset: projection.offset,
                            },
                            color: {
                                axis: 'x',
                                interpolate: colorScale,
                                quantize: scaleQuantity,
                                legend: {
                                    align: align,
                                    position: legendPosition,
                                },
                            },
                        },
                        datalabels: {
                            display: false,
                        }
                    }
                }

                const customConfig = parseJSON(
                    additionalJsonConfig,
                    getLogger('MapChart'),
                    'The JSON object provided to configure the map chart is not valid'
                );


                // When we want to draw the chart again, we need to destroy it first
                if(chart){
                    chart.destroy();
                }
                setChart(new ChoroplethChart(canvas, lodashDeepMerge({...config}, customConfig)));

            }, [mapData, refresh, validatedRawData, width, height, scaleQuantity, colorScale, mapBorderColor, additionalJsonConfig, legendPosition, keyPath, valuePath]);

            // Applying the default params of the component over the div
            return <Box
                // Properties + UI
                component={'div'}
                sx={{
                    width: 1,
                    height: 1,
                    ...ifNotTransparent('borderColor', useProperty($, 'Border Color')),
                    ...ifNotNull('borderRadius', `${useProperty($, 'Border Radius')}px`, useProperty($, 'Border Radius')),
                    borderWidth: useProperty($, 'Border Width'),
                    borderStyle: 'solid',
                    boxSizing: 'border-box',
                    ...ifNotTransparent('backgroundColor', useProperty($, 'Default Color')),
                    ...cssToSxProps(useProperty($, 'CSS Property')),
                    ...jsonToSxProps(useProperty($, 'MUI sx [json]'))
                }}
                onClick={MapChart.clickHandler.bind(this, $, chart)}
            >
                {
                    !mapData && (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight="100%">
                            {!error && <CircularProgress />}
                            {error}
                        </Box>
                    )
                }
                <canvas ref={canvasRef}></canvas>
            </Box>
        }
    }





    /**
     * Triggers the on item click if the function is defined. if on item click is triggered, on click is not.
     * If there is no on item click function defined, on click is triggered all the time
     *
     * @param $ the olympe context
     * @param chart the chart js object used to compute where the user clicked
     * @param evt the html event on click
     */
    static clickHandler($, chart, evt) {
        const lambda = $.get('On Item Click');
        if(lambda !== null){
            const activePoints = chart.getElementsAtEventForMode(evt, 'nearest', { intersect: true }, false);
            if(activePoints.length > 0){
                const [{ datasetIndex, index }] = activePoints;
                const [ctrlFlow, data] = lambda.getInputs();
                $.runner(lambda)
                    .set(data, chart.data.datasets[datasetIndex].data[index])
                    .trigger(ctrlFlow);
            } else {
                $.trigger('On Click');
            }
        } else {
            $.trigger('On Click');
        }

    }

};

registerBrick('018cda1c1b1773180f83', MapChart);
